import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import './TermsOfServicePage.css';

// showBackButton: only passed true when embedded as a "My Profile" sidenav
// section (see ProfilePage.js/SpProfilePage.js) — the standalone /terms
// route (linked from the public footer) keeps its plain marketing header.
const TermsOfServicePage = ({ showBackButton = false }) => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="terms-of-service-page">
      <section className="page-header">
        <div className="container">
          {showBackButton && (
            <button className="terms-back-btn" onClick={() => navigate(-1)}>
              <span>←</span>
              <span>Back</span>
            </button>
          )}
          <h1>Terms of Service</h1>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="terms-content">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using Rialty.ai ("the Platform"), you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Platform.
            </p>

            <h2>2. Description of Service</h2>
            <p>
              Rialty.ai is an intelligent transaction management platform designed to streamline real estate workflows. The Platform provides tools for document management, collaboration, notifications, and AI-driven automation to support real estate professionals, buyers, and sellers.
            </p>

            <h2>3. User Accounts and Registration</h2>
            <h3>3.1 Account Creation</h3>
            <p>
              To use certain features of the Platform, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information as necessary.
            </p>

            <h3>3.2 Account Security</h3>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account or any security breach.
            </p>

            <h3>3.3 Account Eligibility</h3>
            <p>
              You must be at least 18 years old and have the legal capacity to enter into binding contracts to use the Platform.
            </p>

            <h2>4. User Conduct and Prohibited Activities</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Platform for any unlawful purpose or in violation of any applicable laws</li>
              <li>Upload or transmit viruses, malware, or other malicious code</li>
              <li>Interfere with or disrupt the Platform's operation or servers</li>
              <li>Attempt to gain unauthorized access to any part of the Platform</li>
              <li>Impersonate another person or entity</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Scrape, data mine, or extract data without authorization</li>
              <li>Use the Platform for spam or unsolicited communications</li>
            </ul>

            <h2>5. Intellectual Property Rights</h2>
            <h3>5.1 Platform Ownership</h3>
            <p>
              The Platform, including its design, features, functionality, and content, is owned by Rialty.ai and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.
            </p>

            <h3>5.2 User Content</h3>
            <p>
              You retain ownership of content you upload to the Platform ("User Content"). By uploading User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, store, display, and process such content solely for the purpose of providing and improving our services.
            </p>

            <h2>6. Transaction Management and Compliance</h2>
            <p>
              The Platform facilitates real estate transactions but does not provide legal, financial, or professional advice. You are responsible for ensuring compliance with all applicable real estate laws, regulations, and professional standards. We recommend consulting with qualified professionals for legal and financial matters.
            </p>

            <h2>7. Fees and Payments</h2>
            <p>
              Certain features of the Platform may require payment of fees. All fees are non-refundable unless otherwise stated. We reserve the right to modify our pricing structure at any time with reasonable notice to users.
            </p>

            <h2>8. Data and Privacy</h2>
            <p>
              Your use of the Platform is subject to our Privacy Policy, which describes how we collect, use, and protect your information. By using the Platform, you consent to our data practices as described in the Privacy Policy.
            </p>

            <h2>9. SMS / Text Messaging</h2>
            <p>
              By providing your phone number, you consent to receive transactional text messages from Rialty, including invitations to join a transaction workspace and one-time verification codes to confirm your identity before viewing or signing documents. Message frequency varies based on your transaction activity. Message and data rates may apply. No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Reply STOP to opt out at any time, or HELP for help.
            </p>

            <h2>10. Disclaimers and Limitation of Liability</h2>
            <h3>10.1 No Warranty</h3>
            <p>
              The Platform is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
            </p>

            <h3>10.2 Limitation of Liability</h3>
            <p>
              To the maximum extent permitted by law, Rialty.ai shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
            </p>
            <ul>
              <li>Your use or inability to use the Platform</li>
              <li>Unauthorized access to or alteration of your transmissions or data</li>
              <li>Errors, omissions, or inaccuracies in content</li>
              <li>Third-party conduct or content on the Platform</li>
            </ul>

            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Rialty.ai, its affiliates, and their respective officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including legal fees) arising from your use of the Platform, violation of these Terms, or infringement of any third-party rights.
            </p>

            <h2>12. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account and access to the Platform at any time, with or without cause or notice, including if we believe you have violated these Terms. Upon termination, your right to use the Platform will immediately cease.
            </p>

            <h2>13. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on the Platform and updating the "Last Updated" date. Your continued use of the Platform after changes constitutes acceptance of the modified Terms.
            </p>

            <h2>14. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to conflict of law principles. Any disputes arising from these Terms or your use of the Platform shall be resolved through binding arbitration in accordance with [Arbitration Rules], except where prohibited by law.
            </p>

            <h2>15. Severability</h2>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
            </p>

            <h2>16. Contact Information</h2>
            <p>
              If you have any questions or concerns about these Terms of Service, please contact us at:
            </p>
            <p className="contact-info">
              <b>Rialty.ai</b><br />
              Email: legal@rialty.ai<br />
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
